import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const endometrialScratch: ContentBlock<"procedure"> = {
  id: makeId("proc-endometrial-scratch"),
  kind: "procedure",
  tags: ["her", "clinic-visit", "ivf", "fet", "pre-cycle"],
  title: "Endometrial Scratch",
  subtitle:
    "Intentional minor injury to the uterine lining to potentially improve implantation",
  body: {
    procedureName: "Endometrial Scratch (Endometrial Injury)",
    category: "diagnostic",
    description:
      "A procedure where the doctor intentionally creates a small scratch or disruption in the uterine lining in the cycle before embryo transfer. The theory is that the healing process triggers a local inflammatory and immune response that may make the endometrium more receptive to embryo implantation. Evidence is mixed: some studies show benefit for women with recurrent implantation failure, while recent large trials found no overall benefit.",
    howItWorks:
      "A thin catheter (similar to an IUI catheter or pipelle) is inserted through the cervix into the uterus. The doctor gently moves it back and forth to disrupt a small area of the endometrial lining. The procedure takes about 60 seconds. It is typically performed in the luteal phase (day 21-26) of the cycle before the planned embryo transfer.",
    duration: "1-2 minutes",
    costRange: { low: 150, high: 500 },
    currency: "USD",
    anesthesia: "none",
    recoveryTime: "Immediate, mild cramping for a few hours",
    whatToExpect: [
      "Quick office procedure, no anesthesia needed",
      "Sharp cramping during the procedure (similar to a pap smear or IUI, but briefly more intense)",
      "Light spotting for 1-2 days",
      "Take ibuprofen 30-60 minutes before if recommended",
      "Normal activity can resume immediately after",
      "Performed in the cycle BEFORE your transfer cycle",
    ],
    prepInstructions: [
      "Schedule for cycle day 21-26 (luteal phase) of the month before transfer",
      "Take pain relief (ibuprofen 400-600 mg) 30-60 minutes before",
      "No special preparation needed",
      "Confirm with your doctor that you are not pregnant",
    ],
    risks: [
      "Cramping during and shortly after (common)",
      "Light spotting (common)",
      "Infection (very rare)",
      "No proven benefit in all populations, discuss with your RE",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Scratching endométrial",
      description:
        "Geste consistant à créer délibérément une petite lésion de la muqueuse utérine au cours du cycle précédant le transfert d'embryon. L'hypothèse est que la cicatrisation déclenche une réponse inflammatoire et immunitaire locale susceptible de rendre l'endomètre plus réceptif. Les données sont contrastées : certaines études suggèrent un bénéfice en cas d'échec d'implantation à répétition, tandis que de grands essais récents ne retrouvent pas de bénéfice global.",
      howItWorks:
        "Un fin cathéter (semblable à celui d'une IIU ou à une Pipelle) est introduit à travers le col jusque dans l'utérus. Le médecin le déplace doucement d'avant en arrière pour léser une petite zone d'endomètre. Le geste dure environ 60 secondes. Il est généralement réalisé en phase lutéale (J21-J26) du cycle précédant le transfert prévu.",
      duration: "1 à 2 minutes",
      recoveryTime: "Immédiate, crampes légères pendant quelques heures",
      whatToExpect: [
        "Geste en consultation, sans anesthésie",
        "Crampe brève pendant le geste (proche d'un frottis ou d'une IIU, mais plus intense quelques secondes)",
        "Léger spotting pendant 1 à 2 jours",
        "Prenez de l'ibuprofène 30 à 60 minutes avant si recommandé",
        "Activité normale possible immédiatement après",
        "Réalisé pendant le cycle PRÉCÉDANT le cycle de transfert",
      ],
      prepInstructions: [
        "À programmer entre J21 et J26 (phase lutéale) du mois précédant le transfert",
        "Prenez un antalgique (ibuprofène 400 à 600 mg) 30 à 60 minutes avant",
        "Aucune préparation particulière",
        "Assurez-vous avec votre médecin que vous n'êtes pas enceinte",
      ],
      risks: [
        "Crampes pendant et juste après (fréquentes)",
        "Léger spotting (fréquent)",
        "Infection (très rare)",
        "Bénéfice non prouvé dans toutes les populations, à discuter avec votre médecin",
      ],
    },
  },
  sources: [
    {
      title:
        "A Randomized Trial of Endometrial Scratching before In Vitro Fertilization",
      authors: "Lensen S et al.",
      journal: "New England Journal of Medicine",
      year: 2019,
      url: "https://pubmed.ncbi.nlm.nih.gov/30673547/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Endometrial injury in women undergoing in vitro fertilisation (IVF)",
      authors: "Lensen S et al.",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/34110001/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(endometrialScratch);
